import { useMemo } from 'react';
import { omit } from 'lodash';

import { GarmResultsType } from '@insights_module/types/BrandSafety';
import { EpisodeGarmSummary, ISegment } from '@insights_module/types/Episode';
import { ContextualTraceabilityType } from '@insights_module/types/Traceability';
import { aggregateGarmForEpisode } from '@insights_module/utils/episodesUtils';
import { useSelectedEpisodeDetails } from '@insights_module/hooks/useGraphqlAudioDetails';

export const useEpisodeTraceability = () => {
  const { data: selectedEpisodeDetails } = useSelectedEpisodeDetails();

  function getContextualTraceabilityForTimeStamp(
    traceability: ContextualTraceabilityType[],
    timestamp: number
  ) {
    return traceability.find(
      (segment: ContextualTraceabilityType) => segment.start_time === timestamp
    );
  }

  const episodeSegments: ISegment[] = useMemo(() => {
    return (selectedEpisodeDetails?.brand_safety_garm_traceability || []).map((segment, index) => ({
      id: index, // we just need to remember that id can be 0 so we can't use it as a boolean
      startTime: segment.start_time,
      endTime: segment.end_time,
      garm: {
        ...(omit(segment, ['start_time', 'end_time']) as GarmResultsType),
      },
      contextual: {
        categories: omit(
          getContextualTraceabilityForTimeStamp(
            selectedEpisodeDetails?.contextual_data_traceability || [],
            segment.start_time
          ),
          ['start_time', 'end_time']
        )?.categories,
      },
    }));
  }, [
    selectedEpisodeDetails?.brand_safety_garm_traceability,
    selectedEpisodeDetails?.contextual_data_traceability,
  ]);

  const episodeGarmSummary: EpisodeGarmSummary = useMemo(
    () => aggregateGarmForEpisode(episodeSegments),
    [episodeSegments]
  );

  return {
    episodeGarmSummary,
    episodeSegments,
  };
};
